import { ActionButtonFunctions } from "src/config/types";
import {
  NdtClient,
  NdtInput,
  NdtOutput,
} from "@npd-ng/network-diagnostic-tool";
import { TestStatus } from "../factories/ServiceTest";
import { Dispatch, SetStateAction } from "react";
import { secureFetchService } from "../../services/fetchService";

/**
 * Send NDT output to network-readiness-result API endpoint using DPoP-secured fetch
 * Includes automatic retry, timeout, and cryptographic proof-of-possession
 * @param output - NDT test results to send to API
 */
const secureSendToApi = async (output: NdtOutput) => {
  try {
    const reportingEndpoint = `${window.location.origin}/api-edge/v1/devices/ndt-result`;
    output.ndtOutputReceiver = "ADS";
    delete output.ndtLog; // Remove large log data to reduce payload size; adjust as needed based on API requirements
    // retries: 1 means no retries (single attempt only), matching original sendToApi behavior
    const response = await secureFetchService(reportingEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(output),
    }, 1); // Single attempt, no retries
    console.log("[NDT] Secure response:", response);
  } catch (e: any) {
    console.error("[NDT] error occured while posting results to api with DPoP", e);
  }
};

export const handlers: ActionButtonFunctions = {
  setupCommonInput: (
    setStaus: Dispatch<SetStateAction<TestStatus>>,
    inputJson: NdtInput,
    iframeTitle?: string
  ): NdtInput => {
    setStaus({
      status: "Loading",
    });
    const inputParams: NdtInput = inputJson as unknown as NdtInput;
    const elem = document.getElementById("ndtContainer")!;

    if (iframeTitle) {
      labelInjectedIframe(elem, iframeTitle);
    }

    function ExecutionSuccess(e: any) {
      setStaus({
        status: "Finished",
        response: e.detail as NdtOutput,
      });
      console.log(`NDTExecutionCompleted `, e.detail);
      elem.removeEventListener("NDTExecutionCompleted", ExecutionSuccess);
      elem.removeEventListener("NDTExecutionError", ExecutionError);
      // Use secureSendToApi for DPoP-secured network-readiness-result endpoint
      // sendToApi(e.detail); // Original fire-and-forget version
      secureSendToApi(e.detail); // DPoP-secured version with retry and timeout
    }

    function ExecutionError(e: any) {
      //returns NDTOutput
      const status =
        (e.detail as NdtOutput).ndtResults.details.safeList?.statusCode ===
        "7060"
          ? "NetworkError"
          : "Error";
      setStaus({
        status,
        response: e.detail as NdtOutput,
      });
      console.log("NDTExecutionError", e.detail);
      elem.removeEventListener("NDTExecutionCompleted", ExecutionSuccess);
      elem.removeEventListener("NDTExecutionError", ExecutionError);
      // NdtOutput
    }

    elem.addEventListener("NDTExecutionCompleted", ExecutionSuccess);
    elem.addEventListener("NDTExecutionError", ExecutionError);

    //example of switching environment
    const location = window.location.href;
    //how to switch client environment input paramenter by location
    inputParams.hostingClientDetails.clientEnvironment =
      location.indexOf("dev27") > -1 || location.indexOf("localhost") > -1
        ? "dev"
        : location.indexOf("devqc27") > -1
        ? "qa"
        : location.indexOf("prod") > -1 || location.indexOf("ed.gov") > -1
        ? "prod"
        : "stage";
    //on the backend, dcdc is reserved for download center
    inputParams.hostingClientDetails.setupCode = "DCDC";
    inputParams.hostingClientDetails.verificationCode = "DCDC";
    inputParams.hostingClientDetails.clientName = "DownloadCenter";
    inputParams.hostingClientDetails.schoolId = "99900072";
    inputParams.hostingClientDetails.preAssessmentPhase = "DV";
    inputParams.hostingClientDetails.parentElementId = "ndtContainer";
    return inputParams;
  },
  safeListCheck: (
    setStaus: Dispatch<SetStateAction<TestStatus>>,
    inputJson: NdtInput,
    iframeTitle?: string
  ) => {
    console.log(`safeListCheck`);
    const inputParams = handlers.setupCommonInput(setStaus, inputJson, iframeTitle);
    
    const configRef =
      inputParams.environmentMap.find((e) => {
        return (
          e.clientEnvironment ===
          inputParams.hostingClientDetails.clientEnvironment
        );
      })?.configRef || "prod";

    inputParams[configRef].ndtConfiguration.rules.DV = {
      performSafeList: true,
      performContentFiltering:true,
      performSpeedTest: [],
      performSafeListBandwidth: false,
      failJitter: false,
    };

    NdtClient.init(inputParams);
  },
  bandwidthCheck: (
    setStaus: Dispatch<SetStateAction<TestStatus>>,
    inputJson: NdtInput,
    iframeTitle?: string
  ) => {
    console.log(`bandwidthCheck`);
    if (!window.navigator.onLine) {
      setStaus({
        status: "Error",
        response: undefined,
      });
      return;
    }
    setStaus({
      status: "Loading",
    });
    const inputParams = handlers.setupCommonInput(setStaus, inputJson, iframeTitle);
    const configRef =
      inputParams.environmentMap.find((e) => {
        return (
          e.clientEnvironment ===
          inputParams.hostingClientDetails.clientEnvironment
        );
      })?.configRef || "prod";
    inputParams[configRef].ndtConfiguration.rules.DV = {
      performSafeList: false,
      performContentFiltering:false,
      performSpeedTest: [ "enaep-speed-test-client"],
      performSafeListBandwidth: false,
      failJitter: false
    };
    let enaepST;
    for(const agent of inputParams[configRef].ndtConfiguration.speedTest.agents){
      if(agent.name === 'enaep-speed-test-client')
        enaepST = agent;
    }
    if(enaepST){
      enaepST.url = `${window.location.origin}/download-center/enaep-speed-test-custom/index.html`
    }
    NdtClient.init(inputParams);
  },
};

function labelInjectedIframe(container: Element, title: string) {
  const observer = new MutationObserver(() => {
    const iframe = container.querySelector("iframe");
    if (!iframe) return;
    iframe.title = title;
    observer.disconnect();
  });
  observer.observe(container, { childList: true, subtree: true });
}